import GoogleMap, { mapProps } from '@/components/ordering/GoogleMap/GoogleMap';
import { getShops, getShopsByGeoLocation } from '@/app/(ordering)/shop/menu/data/shopdata';
import { shopDataLessProps, shopDataProps } from '@/app/(ordering)/shop/menu/data/shopdata.type';
import { useCallback, useContext, useEffect, useState } from 'react';

import { AuthContext } from '@/components/common/AuthProvider/AuthProvider';
import { BackArrow } from '@/components/common/Icons/BackArrow';
import { Button } from '@/components/common/Button/Button';
import { CartContext } from '@/app/(ordering)/shop/CartProvider/CartProvider';
import { CloseIcon } from '@/components/common/Icons/CloseIcon';
import { InputGroup } from '@/components/common/InputGroup/InputGroup';
import { Loading } from '@/components/common/Icons/Loading';
import { MaterialHeart } from '@/components/common/Icons/MaterialHeart';
import { PickupAddressProps } from '@/app/(ordering)/shop/CartProvider/cartProviderTypes';
import type { PickupCurrentView } from './PickupPanel';
import { StartOrderAddressCard } from '@/components/ordering/StartOrderAddressCard/StartOrderAddressCard';
import { debounce } from 'lodash';
import styles from '@/app/(ordering)/shop/shop.module.css';
import { trackJourneyElement } from '@/utils/eventTracking';

type ShopSearchProps = {
	onShopSelected: (selectedShop: shopDataProps) => void;
	setCurrentView: (view: PickupCurrentView) => void;
};

export default function ShopSearch({ onShopSelected, setCurrentView }: ShopSearchProps) {
	const { userDetails, setFavoriteShop } = useContext(AuthContext);
	const [isSearching, setIsSearching] = useState<boolean>(false);
	const [isSelected, setIsSelected] = useState<boolean>(false);
	const [loadingGeoLoc, setLoadingGeoLoc] = useState<boolean>(false);

	const [shops, setShops] = useState<Array<shopDataProps>>([]);
	const [searchValue, setSearchValue] = useState<string>('');
	const { createTempBasket, address } = useContext(CartContext);
	const [mapPropsValue, setMapPropsValue] = useState<Array<mapProps>>([]);

	useEffect(() => {
		const getLocation = () => {
			setLoadingGeoLoc(true);
			if ('geolocation' in navigator) {
				// Geolocation is supported
				navigator.geolocation.getCurrentPosition(
					function (position) {
						// Location service is enabled
						getShopsByGeoLocation(position.coords.latitude, position.coords.longitude).then(
							(shops) => {
								shops = shops.filter((shop) => shop.status.toLowerCase() !== 'private');
								displayLocationOnMap(shops);
								setLoadingGeoLoc(false);
								setShops(shops);
							}
						);
					},
					function (error) {
						if (error) {
							// Location service is disabled
							setSearchValue(userDetails ? userDetails.loyaltyZipCode : '');
							debouncedSearchForShops(userDetails ? userDetails.loyaltyZipCode : '');
							setLoadingGeoLoc(false);
						}
					}
				);
			} else {
				setLoadingGeoLoc(false);
				// Geolocation is not supported
				console.log('Geolocation is not supported');
			}
		};
		getLocation();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const createTemporaryBasket = async (shop: shopDataLessProps) => {
		const shopAdd: PickupAddressProps = {
			shopId: shop.shopId,
			shopName: shop.shopName,
			streetAddress: shop.address1,
			city: shop.city,
			zipCode: shop.zipCode,
			latitude: shop.latitude,
			longitude: shop.longitude,
		};
		return await createTempBasket({
			type: 'pickup',
			deliveryAddress: null,
			pickupAddress: shopAdd,
			orderDateTime: address.orderDateTime,
		});
	};

	const displayLocationOnMap = (shops: shopDataProps[]) => {
		const locations: Array<mapProps> = [];
		shops.forEach((shop) => {
			if (locations.length < 10 && shop.status.toLowerCase() !== 'private') {
				locations.push({
					latitude: shop.latitude,
					longitude: shop.longitude,
					name: shop.shopName,
				});
			}
		});
		setMapPropsValue([...locations]);
	};

	const searchForShops = (cityStateZip: string) => {
		if (cityStateZip.length > 2) {
			setIsSearching(true);
			setLoadingGeoLoc(true);
			getShops({ cityStateZip }).then((shops) => {
				shops = shops.filter((shop) => shop.status.toLowerCase() !== 'private');
				displayLocationOnMap(shops);
				setShops(shops);
				setIsSearching(false);
				setLoadingGeoLoc(false);
			});
		} else {
			setShops([]);
		}
	};
	// eslint-disable-next-line react-hooks/exhaustive-deps
	const debouncedSearchForShops = useCallback(
		debounce(searchForShops, 250, { leading: false, trailing: true }),
		[]
	);

	const handleChange = (text: string) => {
		setSearchValue(text);
		debouncedSearchForShops(text);
	};

	const handleAddressFieldFocus = () => {
		trackJourneyElement({
			type: 'input',
			action: 'focus',
			location: 'pickup search',
			pathname: window.location.pathname,
			domain: window.location.hostname,
		});
	};

	return (
		<div className={styles.shopSearch}>
			<div className="fixed">
				<button
					aria-label="back"
					onClick={() => {
						setCurrentView('selection');
					}}
				>
					<BackArrow className={styles.backArrow} />
				</button>
			</div>
			<h3 className={`${styles.title} order-title text-center`} data-type-style="subtitle-2">
				Pickup Location
			</h3>
			<InputGroup
				className={`${styles.searchBox} pl-9 text-sm focus:outline-green-600 focus:ring-green-500`}
				label={''}
				inputMode="text"
				value={searchValue}
				placeholder="Search by Zip Code or City, State"
				onChange={(event: React.FormEvent<HTMLInputElement>) =>
					handleChange(event.currentTarget.value)
				}
				onFocus={handleAddressFieldFocus}
			/>
			<button
				className={`${styles.closeIcon} pt-10`}
				onClick={() => {
					handleChange('');
				}}
			>
				{isSearching ? <Loading size="small" fill="gray" /> : <CloseIcon />}
			</button>
			<div className="relative grid gap-2">
				{loadingGeoLoc ? (
					<div className="flex justify-center">
						<Loading size="large" fill="green" />
					</div>
				) : (
					shops.length > 0 && (
						<>
							{mapPropsValue.length > 0 && (
								<div className="mt-8 flex">
									<GoogleMap locations={mapPropsValue} />
								</div>
							)}
							<div key="shopSearch" className="my-8 grid grid-rows-1 gap-2">
								{shops.map((shop: shopDataProps, index: number) => {
									return (
										<StartOrderAddressCard
											disabled={!shop.isAvailable || isSelected} // TODO: disable those shops which don't have dates & times also
											key={index}
											title={shop.shopName}
											addressChildren={
												<div
													className="flex flex-col items-start text-sm leading-6"
													data-type-style="body-3"
													data-ink="light"
												>
													<p>{shop.address1}</p>
													{shop.isAvailable ? (
														<>
															<p>{shop.hoursDescriptionDineIn}</p>
															<p>{shop.hoursDescriptionDriveThru}</p>
														</>
													) : (
														<>
															<p>Dine-in: Temporary Closure</p>
															<p>Drive-thru: Temporary Closure</p>
														</>
													)}
												</div>
											}
											rightIcon={
												userDetails && (
													<Button
														variant="text"
														className="mb-auto flex w-full justify-between"
														disabled={isSelected}
													>
														<MaterialHeart
															className={`mb-auto ms-auto stroke-2 ${userDetails?.favoriteShopId !== shop.shopId ? 'stroke-black' : 'stroke-none'}`}
															key={index}
															fill={
																userDetails?.favoriteShopId === shop.shopId
																	? '#C8102E'
																	: 'var(--color-white)'
															}
															width={'16'}
															height={'16'}
															onClick={() => {
																setFavoriteShop(shop.shopId)
																	.then((success) => {
																		if (success === true) {
																			console.log('set favorite shop success');
																		} else {
																			console.log('set favorite shop failed');
																		}
																	})
																	.catch((error) => {
																		console.log('error = ', error);
																	});
															}}
														/>
													</Button>
												)
											}
											ctaAction={() => {
												// creating temporary basket to fetch wanted times
												setIsSelected(true);
												createTemporaryBasket(shop)
													.then((basket) => {
														setIsSelected(false);
														if (basket) {
															onShopSelected(shop);
														}
													})
													.finally(() => {
														setIsSelected(false);
													});
											}}
										/>
									);
								})}
							</div>
						</>
					)
				)}
			</div>
		</div>
	);
}
